import {useEffect, useMemo, useState} from "react";
import {useAppSelector} from "../../../hooks/redux";
import {maskVin} from "../../board/model/format";
import {isPageActive, setFaviconDot} from "./browser";
import {blinkingTitle, tabTitle, untakenAlertVin} from "./rules";
import {NTX} from "./texts";

/** Вкладка на экране и в фокусе — с перерисовкой при смене. */
function usePageActive(): boolean {
    const [active, setActive] = useState(isPageActive);
    useEffect(() => {
        const update = () => setActive(isPageActive());
        window.addEventListener("focus", update);
        window.addEventListener("blur", update);
        document.addEventListener("visibilitychange", update);
        return () => {
            window.removeEventListener("focus", update);
            window.removeEventListener("blur", update);
            document.removeEventListener("visibilitychange", update);
        };
    }, []);
    return active;
}

/**
 * Заголовок вкладки `({N}) ATOM EoL` по непрочитанному (треды + общий чат).
 * Пока есть алерт, который никто не взял, а вкладка скрыта или не в фокусе, —
 * раз в секунду чередует «Алерт · {хвост VIN}» и обычный, на значке вкладки
 * красная точка (S4.2). Подключён на уровне приложения (AppRealtime).
 */
export function useTabTitle() {
    const unread = useAppSelector((s) => s.chat.unread);
    const cars = useAppSelector((s) => s.board.cars);
    const me = useAppSelector((s) => s.users.current?.id ?? null);
    const pageActive = usePageActive();
    const base = tabTitle(unread);
    // Свой поднятый алерт не мигает (S4.3), как и не звучит.
    const alertVin = useMemo(() => untakenAlertVin(Object.values(cars), me), [cars, me]);
    const alertTail = alertVin ? maskVin(alertVin) : null;
    const blinking = !!alertTail && !pageActive;
    const [tick, setTick] = useState(0);

    useEffect(() => {
        setTick(0);
        if (!blinking) return;
        const id = window.setInterval(() => setTick((n) => n + 1), 1000);
        return () => window.clearInterval(id);
    }, [blinking]);

    const title = blinkingTitle(base, alertTail, pageActive, tick);
    useEffect(() => {
        document.title = title;
    }, [title]);

    useEffect(() => {
        void setFaviconDot(blinking);
    }, [blinking]);

    useEffect(
        () => () => {
            document.title = NTX.brand;
            void setFaviconDot(false);
        },
        [],
    );
}
